import { useEffect, useState, useMemo } from 'react';
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components';
import { getDefaultTheme, getThemeToProvide, convertLegacyThemeName } from './ThemeProvider.helpers';
import { ThemeMetadataContextProvider } from './ThemeMetadataContext';
import devLogger from 'react-utils/devLogger';
import TrellisStyleSheetManager from '../TrellisStyleSheetManager/TrellisStyleSheetManager';
import { getLocalThemeSettings } from '../../utils/getLocalThemeSettings';
import { jsx as _jsx } from "react/jsx-runtime";
const TRELLIS_THEME_EVENT = 'trellis:theme';
let globalThemeSettings;
let initialThemePromise;
export function setupThemeSettings() {
  globalThemeSettings = getLocalThemeSettings();

  // we start this first request much before render by putting it at module scope
  if (isFullThemeSettings(globalThemeSettings)) {
    const {
      themeName,
      themeMode
    } = convertLegacyThemeName({
      themeName: globalThemeSettings.themeName,
      themeMode: globalThemeSettings.themeMode
    });
    initialThemePromise = getThemeToProvide(Object.assign({}, globalThemeSettings, {
      themeName,
      themeMode
    }));
  } else {
    initialThemePromise = undefined;
  }
}

// we want to get settings in module scope so that they're only ever retrieved once, and asap
setupThemeSettings();
function isFullThemeSettings(themeSettings) {
  return themeSettings.themeName !== undefined && themeSettings.themeMode !== undefined;
}
function mergeThemeSettings(themeNameProp, themeModeProp, themeSettings) {
  const {
    themeName: themeNameSetting,
    themeMode: themeModeSetting
  } = themeSettings;
  return Object.assign({}, themeSettings, {
    themeName: themeNameSetting || themeNameProp || 'canvas',
    themeMode: themeModeSetting || themeModeProp || 'light'
  });
}
function isSameTheme(a, b) {
  if (!isFullThemeSettings(a) || !isFullThemeSettings(b)) {
    return false;
  }
  return a.themeName === b.themeName && a.themeMode === b.themeMode;
}
function getHasDefaultSettings(settings) {
  return settings.themeVersion.type === 'builtin' && settings.themeName === 'canvas' && settings.themeMode === 'light' && Object.keys(settings.themeOverrides).length === 0;
}

/**
 * The `ThemeProvider` provides the theme object for your app via
 * React Context. Foundations components **will not work without it**.
 * This `ThemeProvider` is used by the `AppProvider` component,
 * which is the top-level component that should wrap your entire app.
 * However, it can also be used on its own lower in your component tree
 * if you need to override the theme for a specific subtree. This could
 * be useful for opt-in user testing purposes.
 *
 * This component is also used by `hs-test-utils` to wrap all unit test
 * component trees.
 */
export const ThemeProvider = ({
  themeName: _themeName = 'canvas',
  themeMode: _themeMode = 'light',
  children
}) => {
  const {
    themeName,
    themeMode
  } = convertLegacyThemeName({
    themeName: _themeName,
    themeMode: _themeMode
  });
  const mergedThemeSettings = useMemo(() => mergeThemeSettings(themeName, themeMode, globalThemeSettings), [themeName, themeMode]);
  const hasDefaultSettings = getHasDefaultSettings(mergedThemeSettings);
  const [theme, setTheme] = useState(() => {
    // if there are no theme settings, we can render the theme immediately
    // if no theme is available on first render this will break RTL tests
    if (initialThemePromise) {
      return undefined;
    }
    return getDefaultTheme();
  });
  useEffect(() => {
    const handleUpdateTheme = themePromise => {
      themePromise.then(setTheme).catch(error => {
        devLogger.warn({
          message: `Failed to load theme with error: ${error}`,
          key: 'foundations-theming:theme-provider:theme-load-failure'
        });
        setTheme(getDefaultTheme());
      });
    };
    if (hasDefaultSettings) {
      setTheme(getDefaultTheme());
    } else {
      // the initThemePromise is created with localStorage settings, so if settings are the same we can reuse it
      const themePromise = isSameTheme(mergedThemeSettings, globalThemeSettings) && initialThemePromise ? initialThemePromise : getThemeToProvide(mergedThemeSettings);
      handleUpdateTheme(themePromise);
    }
    const handleUpdateEvent = event => {
      if (event.data.type !== TRELLIS_THEME_EVENT) {
        return;
      }
      const newThemeSettings = getLocalThemeSettings();
      const newMergedThemeSettings = mergeThemeSettings(themeName, themeMode, newThemeSettings);
      handleUpdateTheme(getThemeToProvide(newMergedThemeSettings));
    };
    window.addEventListener('message', handleUpdateEvent);
    return () => {
      window.removeEventListener('message', handleUpdateEvent);
    };
  }, [hasDefaultSettings, mergedThemeSettings, themeMode, themeName]);
  return theme ? /*#__PURE__*/_jsx(TrellisStyleSheetManager, {
    children: /*#__PURE__*/_jsx(StyledComponentsThemeProvider, {
      theme: theme,
      children: /*#__PURE__*/_jsx(ThemeMetadataContextProvider, {
        themeName: themeName,
        themeMode: themeMode,
        children: children
      })
    })
  }) : null;
};